// Pure inbox helpers: tab membership, filters, grouping by task and day buckets.
import { differenceInCalendarDays } from 'date-fns';
import type { Notification, Status } from '@shared/types';
import type { Tables } from '@/lib/store';
import { effectiveStatuses } from '@/lib/hierarchy';

export type InboxTab = 'primary' | 'other' | 'later' | 'cleared';
export type InboxFilter = 'all' | 'mentions' | 'assigned' | 'comments' | 'status';

export const INBOX_TABS: { key: InboxTab; label: string; empty: string; emptyHint: string }[] = [
  { key: 'primary', label: 'Primary', empty: "You're all caught up", emptyHint: 'Mentions, assignments and comments on your work show up here.' },
  { key: 'other', label: 'Other', empty: "You're all caught up", emptyHint: 'Status, date and priority updates on tasks you watch show up here.' },
  { key: 'later', label: 'Later', empty: 'Nothing saved for later', emptyHint: 'Snooze a notification with the clock button to come back to it here.' },
  { key: 'cleared', label: 'Cleared', empty: 'No cleared notifications', emptyHint: 'Notifications you clear are kept here so you can restore them.' },
];

export const INBOX_FILTERS: { key: InboxFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'mentions', label: 'Mentions' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'comments', label: 'Comments' },
  { key: 'status', label: 'Status changes' },
];

export function inTab(n: Notification, tab: InboxTab): boolean {
  switch (tab) {
    case 'primary':
      return n.bucket === 'primary' && !n.clearedAt && !n.laterAt;
    case 'other':
      return n.bucket === 'other' && !n.clearedAt && !n.laterAt;
    case 'later':
      return !!n.laterAt && !n.clearedAt;
    case 'cleared':
      return !!n.clearedAt;
  }
}

export function matchesFilter(n: Notification, filter: InboxFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'mentions':
      return n.kind === 'mention';
    case 'assigned':
      return n.kind === 'assigned' || n.kind === 'assigned_comment';
    case 'comments':
      return n.kind === 'comment' || n.kind === 'reply' || n.kind === 'assigned_comment';
    case 'status':
      return n.kind === 'status';
  }
}

/** Whether the notification's target still exists in this workspace. */
export function belongsToWorkspace(db: Tables, n: Notification, ws: string): boolean {
  if (n.taskId) {
    const t = db.tasks[n.taskId];
    return !!t && t.workspaceId === ws;
  }
  const channelId = typeof n.data?.channelId === 'string' ? n.data.channelId : null;
  if (channelId) {
    const ch = db.channels[channelId];
    return !!ch && ch.workspaceId === ws;
  }
  return false;
}

export interface InboxGroup {
  key: string;
  taskId: string | null;
  channelId: string | null;
  /** newest first */
  items: Notification[];
  latest: Notification;
  unread: boolean;
  at: number;
}

export function channelOf(n: Notification): string | null {
  return typeof n.data?.channelId === 'string' ? n.data.channelId : null;
}

/** One row per task (or chat channel) showing its latest event, newest rows first. */
export function groupNotifications(list: Notification[], byTask: boolean): InboxGroup[] {
  const map = new Map<string, Notification[]>();
  for (const n of list) {
    const key = byTask ? (n.taskId ? `t:${n.taskId}` : channelOf(n) ? `c:${channelOf(n)}` : `n:${n.id}`) : `n:${n.id}`;
    const arr = map.get(key);
    if (arr) arr.push(n);
    else map.set(key, [n]);
  }
  const groups: InboxGroup[] = [];
  for (const [key, items] of map) {
    items.sort((a, b) => b.createdAt - a.createdAt);
    const latest = items[0];
    groups.push({
      key,
      taskId: latest.taskId,
      channelId: channelOf(latest),
      items,
      latest,
      unread: items.some((i) => !i.readAt),
      at: latest.createdAt,
    });
  }
  return groups.sort((a, b) => b.at - a.at);
}

export type DayBucket = 'today' | 'yesterday' | 'week' | 'older';

export const DAY_BUCKETS: { key: DayBucket; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'older', label: 'Older' },
];

export function dayBucket(ts: number, now = Date.now()): DayBucket {
  const diff = differenceInCalendarDays(now, ts);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 7) return 'week';
  return 'older';
}

export function bucketize<T extends { at: number }>(rows: T[]): { key: DayBucket; label: string; rows: T[] }[] {
  const out = DAY_BUCKETS.map((b) => ({ ...b, rows: [] as T[] }));
  for (const r of rows) out.find((b) => b.key === dayBucket(r.at))!.rows.push(r);
  return out.filter((b) => b.rows.length);
}

/** Plain text from stored HTML without executing anything (DOMParser documents are inert). */
export function htmlToText(html: string): string {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  } catch {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

/** Find a status by id: the task's list first, then anywhere (the task may have moved). */
export function findStatus(db: Tables, listId: string | undefined, id: unknown): Status | undefined {
  if (typeof id !== 'string') return undefined;
  if (listId) {
    const s = effectiveStatuses(db, listId).find((x) => x.id === id);
    if (s) return s;
  }
  for (const sp of Object.values(db.spaces)) {
    const s = sp.statuses.find((x) => x.id === id);
    if (s) return s;
  }
  for (const f of Object.values(db.folders)) {
    const s = f.statuses?.find((x) => x.id === id);
    if (s) return s;
  }
  for (const l of Object.values(db.lists)) {
    const s = l.statuses?.find((x) => x.id === id);
    if (s) return s;
  }
  return undefined;
}

export function channelTitle(db: Tables, channelId: string, me: string): string {
  const ch = db.channels[channelId];
  if (!ch) return 'Chat';
  if (ch.kind === 'dm') {
    const other = ch.memberIds.find((u) => u !== me) ?? me;
    return db.users[other]?.name ?? 'Direct message';
  }
  return ch.name;
}
